import { Container } from "inversify";
import { type FC, type PropsWithChildren, useRef } from "react";
import AlarmService from "@/src/application/services/alarm-service";
import ApiService from "@/src/application/services/api-service";
import AuthService, { AuthenticationStatus } from "@/src/application/services/auth-service";
import CompositeDataService from "@/src/application/services/composite-data-service";
import DeviceService from "@/src/application/services/device-service";
import MeasurementService from "@/src/application/services/measurement-service";
import NotificationService from "@/src/application/services/notification-service";
import { registerN3Backend } from "@/src/application/services/registers/n3-backend";
import RouteService from "@/src/application/services/route-service";
import UserService from "@/src/application/services/user-service";
import AlarmStore from "@/src/application/store/alarm-store";
import AppState from "@/src/application/store/app-state";
import brandConfig from "@/src/config/brand";
import { type ContainerProxy, DIContext } from "@/src/infrastructure/di";
import AlarmServiceImpl from "@/src/infrastructure/services/alarm-service-impl";
import ApiServiceImpl from "@/src/infrastructure/services/api-service-impl";
import AuthServiceImpl from "@/src/infrastructure/services/auth-service-impl";
import CompositeDataServiceImpl from "@/src/infrastructure/services/composite-data-service-impl";
import DeviceServiceImpl from "@/src/infrastructure/services/device-service-impl";
import FirebaseNotificationService from "@/src/infrastructure/services/firebase-notification-service";
import MeasurementServiceImpl from "@/src/infrastructure/services/measurement-service-impl";
import RouteServiceImpl from "@/src/infrastructure/services/route-service-impl";
import UserServiceImpl from "@/src/infrastructure/services/user-service-impl";

const ContainerProvider: FC<PropsWithChildren> = ({ children }) => {
  const containerRef = useRef<ContainerProxy | null>(null);
  if (!containerRef.current) {
    const container = new Container();

    const runtimeConfig = (window as unknown as { __CONFIG__: Record<string, string> })[
      "__CONFIG__"
    ];
    const apiUrl = runtimeConfig?.API_URL || brandConfig.apiUrl;

    container.bind(AppState).toSelf().inSingletonScope();
    container.bind(AlarmStore).toSelf().inSingletonScope();
    container.bind<string>("BASE_API").toConstantValue(apiUrl ?? "");
    container.bind<ApiService>(ApiService).to(ApiServiceImpl).inSingletonScope();
    registerN3Backend(container);
    container.bind<AuthService>(AuthService).to(AuthServiceImpl).inSingletonScope();
    container.bind<UserService>(UserService).to(UserServiceImpl).inSingletonScope();
    container.bind<DeviceService>(DeviceService).to(DeviceServiceImpl).inSingletonScope();
    container
      .bind<MeasurementService>(MeasurementService)
      .to(MeasurementServiceImpl)
      .inSingletonScope();
    container.bind<RouteService>(RouteService).to(RouteServiceImpl).inSingletonScope();
    container.bind(AlarmService).to(AlarmServiceImpl).inSingletonScope();
    container
      .bind<NotificationService>(NotificationService)
      .to(FirebaseNotificationService)
      .inSingletonScope();
    container
      .bind<CompositeDataService>(CompositeDataService)
      .to(CompositeDataServiceImpl)
      .inSingletonScope();

    const apiService = container.get<ApiServiceImpl>(ApiService as any);
    const authService = container.get<AuthServiceImpl>(AuthService as any);
    apiService.setUnauthorizedHandler(() => {
      authService.setStatus(AuthenticationStatus.NotAuthenticated);
    });

    containerRef.current = {
      container,
      level: 0,
      version: 0,
    };

    if (import.meta.env.DEV) {
      (
        window as unknown as {
          __E2E_CONTAINER__: typeof container;
          __E2E_AUTH__: typeof authService;
        }
      ).__E2E_CONTAINER__ = container;
      (
        window as unknown as {
          __E2E_CONTAINER__: typeof container;
          __E2E_AUTH__: typeof authService;
        }
      ).__E2E_AUTH__ = authService;
    }
  }
  return <DIContext.Provider value={containerRef.current}>{children}</DIContext.Provider>;
};

export default ContainerProvider;
